import { useState, useRef } from 'react';
import { pushDataLayerForEvent } from '@lib/gtag';
import Image from '../atoms/Image';
import { Spinner } from '@heroui/react';
import { format } from 'date-fns';
import {
  PencilSimpleIcon,
  TrashSimpleIcon,
  ChatCircleDotsIcon,
  CaretLeftIcon,
  CaretRightIcon,
} from '@phosphor-icons/react';

export const platformImages = {
  zoom: '/images/Zoom.svg',
  teams: '/images/teams-logo.svg',
  youtube: '/images/youtube-icon.svg',
  ipa: '/images/in_person.svg',
  meet: '/images/meet-icon.svg',
  twitch: '/images/twitch-icon.svg',
  'other-online-events': '/images/OtherOnlinePlatforms.svg',
  hybrid: '/images/hybrid.svg',
  vimeo: '/images/vimeo-icon.svg',
  webex: '/images/webex-icon.svg',
  linkedin: '/images/linkedin-icon.svg',
  restream: '/images/restream.svg',
  streamyard: '/images/streamyard.png',
  airmeet: '/images/airmeet.svg',
  kick: '/images/kick.png',
  teams_web_bot: '/images/teams-logo.svg',
  adobe_connect: '/images/adobe-connect-logo.svg',
};

const PAGE_SIZE = 30;

const CardList = ({
  stream,
  goToFantasticPage,
  page,
  totalCount,
  onPageChange,
  onRename,
  onAskDelete,
  isLoading,
  isPageLoading,
}) => {
  const [renamingId, setRenamingId] = useState(null);
  const [draft, setDraft] = useState('');
  const renamingIdRef = useRef(null);

  if (isLoading) {
    return (
      <div className="flex w-full h-full justify-center items-center">
        <Spinner size="md" color="default" />
      </div>
    );
  }

  const totalPages = Math.ceil(totalCount / PAGE_SIZE);

  const handleRowClick = (item) => {
    if (renamingIdRef.current) return;
    pushDataLayerForEvent('analyze analytics', { analyticsStreamId: item.id });
    goToFantasticPage(item.id);
    localStorage.setItem('analyticsScrollId', JSON.stringify({ id: `${item.id}`, page }));
  };

  const startRename = (item, e) => {
    e.stopPropagation();
    renamingIdRef.current = item.id;
    setRenamingId(item.id);
    setDraft(item.title);
  };

  const commitRename = (id) => {
    if (draft.trim()) onRename(id, draft.trim());
    renamingIdRef.current = null;
    setRenamingId(null);
  };

  const cancelRename = () => {
    renamingIdRef.current = null;
    setRenamingId(null);
  };

  return (
    <div className="relative flex flex-col bg-content2 border border-divider rounded-xl overflow-hidden">
      {isPageLoading && (
        <div className="absolute inset-0 z-10 flex items-center justify-center bg-content2/60 rounded-xl">
          <Spinner size="sm" color="default" />
        </div>
      )}

      <div className="overflow-auto max-h-[calc(100vh-100px)]">
        <table className="w-full border-collapse">
          <thead className="sticky top-0 z-10 bg-content2">
            <tr className="border-b border-divider">
              <th className="text-left text-[11px] font-semibold uppercase tracking-widest text-default-400 px-4 py-3">Session</th>
              <th className="text-left text-[11px] font-semibold uppercase tracking-widest text-default-400 px-4 py-3">Time</th>
              <th className="text-left text-[11px] font-semibold uppercase tracking-widest text-default-400 px-4 py-3">Date</th>
              <th className="text-left text-[11px] font-semibold uppercase tracking-widest text-default-400 px-4 py-3">Comments</th>
              <th className="text-right text-[11px] font-semibold uppercase tracking-widest text-default-400 px-4 py-3">Actions</th>
            </tr>
          </thead>
          <tbody>
            {stream.map((item) => {
              const isRenaming = renamingId === item.id;
              return (
                <tr
                  id={item.id}
                  key={item.id}
                  className="border-t border-divider hover:bg-content3 cursor-pointer group transition-colors"
                  onClick={() => handleRowClick(item)}
                >
                  {/* SESSION */}
                  <td className="px-4 py-3 max-w-xs">
                    <div className="flex items-center gap-3 min-w-0">
                      <Image
                        src={platformImages[item.platform_type]}
                        width={24}
                        alt={`${item.platform_type}-icon`}
                        className="shrink-0"
                      />
                      {isRenaming ? (
                        <input
                          autoFocus
                          className="text-sm font-semibold bg-content1 border border-primary rounded px-2 py-1 outline-none w-full"
                          value={draft}
                          onChange={e => setDraft(e.target.value)}
                          onPointerDown={e => e.stopPropagation()}
                          onClick={e => e.stopPropagation()}
                          onBlur={() => commitRename(item.id)}
                          onKeyDown={e => {
                            if (e.key === 'Enter') { e.preventDefault(); commitRename(item.id); }
                            if (e.key === 'Escape') cancelRename();
                          }}
                        />
                      ) : (
                        <p className="text-sm font-semibold truncate group-hover:text-primary">
                          {item.title}
                        </p>
                      )}
                    </div>
                  </td>

                  {/* TIME */}
                  <td className="px-4 py-3 text-[13px] text-default-500 whitespace-nowrap">
                    {format(new Date(item.start_time || item.started_on), 'hh:mm a')}
                  </td>

                  {/* DATE */}
                  <td className="px-4 py-3 text-[13px] text-default-500 whitespace-nowrap">
                    {format(new Date(item.start_time || item.started_on), 'MMM dd, yyyy')}
                  </td>

                  {/* COMMENTS */}
                  <td className="px-4 py-3">
                    <span className="flex items-center gap-1.5 text-[13px] text-default-500">
                      <ChatCircleDotsIcon size={14} className="text-default-400" />
                      {item.comment_count ?? 0}
                    </span>
                  </td>

                  {/* ACTIONS */}
                  <td className="px-4 py-3">
                    <div
                      className="flex items-center gap-1 justify-end"
                      onClick={e => e.stopPropagation()}
                    >
                      <button
                        title="Rename"
                        className="p-1.5 rounded-md text-default-400 hover:text-default-700 hover:bg-default-100 transition-colors"
                        onClick={e => startRename(item, e)}
                      >
                        <PencilSimpleIcon size={15} />
                      </button>
                      <button
                        title="Delete"
                        className="p-1.5 rounded-md text-default-400 hover:text-danger hover:bg-danger-50 transition-colors"
                        onClick={e => { e.stopPropagation(); onAskDelete(item); }}
                      >
                        <TrashSimpleIcon size={15} />
                      </button>
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {/* Pagination footer */}
      <div className="flex items-center justify-between px-4 py-3 border-t border-divider text-[12.5px] text-default-400 shrink-0">
        <span>
          Showing{' '}
          <span className="font-medium text-default-600">{stream.length}</span>{' '}
          of{' '}
          <span className="font-medium text-default-600">{totalCount}</span>
        </span>
        <div className="flex items-center gap-2">
          <span>Page {page} of {totalPages || 1}</span>
          <button
            className="w-7 h-7 flex items-center justify-center rounded-md hover:bg-default-100 disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
            onClick={() => onPageChange(page - 1)}
            disabled={page <= 1}
          >
            <CaretLeftIcon size={14} />
          </button>
          <button
            className="w-7 h-7 flex items-center justify-center rounded-md hover:bg-default-100 disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
            onClick={() => onPageChange(page + 1)}
            disabled={page >= totalPages}
          >
            <CaretRightIcon size={14} />
          </button>
        </div>
      </div>
    </div>
  );
};

export default CardList;
